import { ErrorHelper } from "./errorHelpers";

export type StringifyReplacerFn = (this: unknown, key: string, value: unknown) => unknown;

export function isString(value: unknown): value is string
{
    return (typeof value === "string" || value instanceof String);
}

export interface StringifyOptions
{
    /** */
    readonly propertiesToIgnore?: Array<string>;
    /** Adds indentation, white space, and line break characters to the return-value JSON text to make it easier to read. */
    readonly space?: string | number;
    /** Adds a replacer function that will remove repeated object values */
    readonly cacheValues?: boolean;
    /** Adds a replacer function that will ignore all properties that begin with '$' */
    readonly ignoreSpecialProperties?: boolean;
}

class StringHelpersImpl
{
    /** camelCase */
    toCamelCase(value: string): string
    {
        const deKababed = value.replace(/[ -]([a-z])/g, x => x[1].toUpperCase());
        return deKababed[0].toLowerCase() + deKababed.slice(1);
    }

    /** PascalCase */
    toPascalCase(value: string): string
    {
        const pattern = /[ -](\w)/g;
        const camelCased = value.replace(pattern, x => x[1].toUpperCase());
        return camelCased[0].toUpperCase() + camelCased.slice(1);
    }

    /** kebab-case */
    toKebabCase(value: string): string
    {
        const pattern = /([a-z])([A-Z])/g;
        return value.replace(pattern, "$1-$2")
            .replace(/\s+/g, "-")
            .toLowerCase();
    }

    toStandardCase(value: string): string
    {
        const pattern = /([a-z])([A-Z])/g;
        return value.replace(pattern, "$1 $2")
            .replace(/\s+/g, " ")
            .toLowerCase();
    }

    /**
     * Attempts to parse the given string as JSON.
     * @returns The parsed JSON object if the given string was valid JSON, otherwise `undefined`.
     */
    parse<T = unknown>(value: string): T | undefined
    {
        try
        {
            return JSON.parse(value) as T;
        }
        catch (error)
        {
            return undefined;
        }
    }

    /**
     * Tests if a string is either "true" or "false", ignoring case.
     * @param value string to be tested
     * @returns `true` if the string was either "true" or "false", ignoring case; `false` otherwise.
     */
    isBoolean(value: string): boolean
    {
        return /true|false/i.test(value?.toLowerCase());
    }

    /**
     * Parses a string to a boolean value.
     * @param value string to be tested
     * @returns `true` if the string was "true", ignoring case; `false` otherwise.
     */
    parseBoolean(value: string): boolean
    {
        return /true/i.test(value?.toLowerCase());
    }

    /**
     * Tests if a string contains a valid currency amount. Accepts both standard negative notation and parenthesized
     * notations.
     * @param value
     * @returns `true` if the string was a valid currency amount.
     */
    isCurrency(value: string): boolean
    {
        return /(\$[(-]?|[)-]?\$)(\d{1,3},?)+(\.\d+)?\)?/.test(value);
    }

    /**
     * Strips all non-numeric characters out of the given string and attempts to parse the remainder as a number.
     * @param value Value to be parsed as a number
     * @returns the parsed number if there were any numeric characters remaining, `NaN` otherwise.
     */
    parseDate(value: string): Date | undefined
    {
        const milliseconds = Date.parse(value);
        return !isNaN(milliseconds)
            ? new Date(milliseconds)
            : undefined;
    }

    /**
     * Strips all non-numeric characters out of the given string and attempts to parse the remainder as a number.
     * @param value Value to be parsed as a number
     * @returns the parsed number if there were any numeric characters remaining, `NaN` otherwise.
     */
    parseNumber(value: string): number
    {

        const stripped = value.replace(/[^-\d.]/g, "");
        return stripped
            ? parseFloat(stripped)
            : NaN;
    }

    /**
     * Return a string representation using safe replacer functions that avoids traversing circular structures and
     * removing unwanted fields.
     */
    cachingStringify<T = unknown>(input: T, propertiesToIgnore?: Array<string>, space?: string | number): string
    {
        return this.stringify(input, { propertiesToIgnore, space, cacheValues: true });
    }

    /**
     * Serializes the given input a constructed replacer function that will ignore an client-side special properties, as
     * well as any properties that are listed in the given array `propertiesToIgnore`, and will removes nested
     * references.
     * Note: For more control over how the replacer function is constructed use `stringify(string, options)` instead.
     */
    stringify<T = unknown>(input: T, propertiesToIgnore?: Array<string>, space?: string | number): string;
    stringify<T = unknown>(input: T, opts: StringifyOptions): string;
    /**
     * Return a string representation using safe replacer functions that avoids traversing circular structures and
     * can removing unwanted fields.
     */
    stringify<T = unknown>(input: T, optsOrPropertiesToIgnore?: StringifyOptions | Array<string>, space?: string | number): string
    {
        let propertiesToIgnore: Array<string>;
        let cacheValues: boolean;
        let ignoreSpecialProperties: boolean;
        if (optsOrPropertiesToIgnore && typeof optsOrPropertiesToIgnore === "object")
        {
            propertiesToIgnore = (optsOrPropertiesToIgnore as StringifyOptions).propertiesToIgnore;
            space = (optsOrPropertiesToIgnore as StringifyOptions).space;
            cacheValues = (optsOrPropertiesToIgnore as StringifyOptions).cacheValues;
            ignoreSpecialProperties = (optsOrPropertiesToIgnore as StringifyOptions).ignoreSpecialProperties;
        }
        else
        {
            propertiesToIgnore = optsOrPropertiesToIgnore as Array<string>;
            cacheValues = false;
            ignoreSpecialProperties = true;
        }

        // The nesting order matters here.. the deepest replacer should be executed first
        const cachingReplacer = cacheValues
            ? this.getCachingReplacer()
            : undefined;

        const ignoredPropertyReplacer = propertiesToIgnore
            ? this.getIgnorePropertyReplacer(propertiesToIgnore, cachingReplacer)
            : cachingReplacer;

        const nullPropertyReplacer = this.getNullPropertyReplacer(ignoredPropertyReplacer);

        const clientPropertyReplacer = ignoreSpecialProperties
            ? this.getClientPropertyReplacer(nullPropertyReplacer)
            : nullPropertyReplacer;

        // TODO - i think the cachingReplace should be the outer most replacer?
        const replacerFn = this.getErrorStringifierReplacer(clientPropertyReplacer);

        return JSON.stringify(input, replacer, space);

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        function replacer(this: unknown, _key: string, value: unknown)
        {
            value = replacerFn(_key, value);

            return value;
        }
    }

    /**
     * Returns a function that will track objects already stringified and not attempt a second pass.
     *
     * DO NOT HOLD ON TO THIS FUNCTION AS IT WILL BE A MEMORY LEAK....
     */
    private getCachingReplacer(delegateReplacer?: StringifyReplacerFn): StringifyReplacerFn
    {
        const cache = new WeakSet();
        const delegate = delegateReplacer || ((_k, v) => v);
        return cachingReplacer;

        function cachingReplacer(this: unknown, key: string, value: unknown): unknown
        {
            value = delegate(key, value);
            if (!!value && typeof value === "object")
            {
                if (cache.has(value))
                {
                    return undefined;
                }
                else
                {
                    cache.add(value);
                }
            }

            return value;
        }
    }

    /**
     * Returns a function that will remove property values for the given propertyNames.
     *
     * DO NOT HOLD ON TO THIS FUNCTION AS IT WILL BE A MEMORY LEAK....
     */
    private getIgnorePropertyReplacer(propertyNames: Array<string>, delegateReplacer?: StringifyReplacerFn): StringifyReplacerFn
    {
        const ignoredNames = new Set(propertyNames);
        const delegate = delegateReplacer || ((_k, v) => v);
        return ignoringReplacer;

        function ignoringReplacer(this: unknown, key: string, value: unknown): unknown
        {
            value = delegate(key, value);
            if (ignoredNames.has(key))
            {
                return undefined;
            }

            return value;
        }
    }

    /**
     * Returns a function that will remove properties that have a `undefined` or `null` value.
     * This explicitly looks for `undefined` and `null`, it does not rely on falsy-ness of the value.
     *
     * DO NOT HOLD ON TO THIS FUNCTION AS IT WILL BE A MEMORY LEAK....
     */
    private getNullPropertyReplacer(delegateReplacer?: StringifyReplacerFn): StringifyReplacerFn
    {
        const delegate = delegateReplacer || ((_k, v) => v);
        return nullPropertyReplacer;

        function nullPropertyReplacer(this: unknown, key: string, value: unknown): unknown
        {
            value = delegate(key, value);
            if (value === undefined || value === null)
            {
                return undefined;
            }

            return value;
        }
    }

    /**
     * Returns a function that will remove property values that are client-only properties.
     * Client properties are any property whose name begins with the `$` symbol.
     * DO NOT HOLD ON TO THIS FUNCTION AS IT WILL BE A MEMORY LEAK....
     */
    private getClientPropertyReplacer(delegateReplacer?: StringifyReplacerFn): StringifyReplacerFn
    {
        const delegate = delegateReplacer || ((_k, v) => v);
        return clientPropertyReplacer;

        function clientPropertyReplacer(this: unknown, key: string, value: unknown): unknown
        {
            value = delegate(key, value);
            if (/^\$.*$/.test(key))
            {
                return undefined;
            }

            return value;
        }
    }

    /**
     * Returns a function that will remove property values that are client-only properties.
     * Client properties are any property whose name begins with the `$` symbol.
     * DO NOT HOLD ON TO THIS FUNCTION AS IT WILL BE A MEMORY LEAK....
     */
    private getErrorStringifierReplacer(delegateReplacer?: StringifyReplacerFn): StringifyReplacerFn
    {
        const delegate = delegateReplacer || ((_k, v) => v);
        return clientPropertyReplacer;

        function clientPropertyReplacer(this: unknown, key: string, value: unknown): unknown
        {
            value = delegate(key, value);
            if (value instanceof Error)
            {
                return ErrorHelper.toJson(value);
            }

            return value;
        }
    }
}

export const StringHelper = new StringHelpersImpl();
